import React, { useCallback, useEffect, useRef, useState } from "react";

import { ApiCreditCardsResponse } from "@/api/creditCard";
import { CREDIT_CARD_PAGE_CHANGE_KEYWORDS } from "@/constants/eventName";
import useEvent from "@/hooks/useEvent";
import { useListenEvent } from "@/hooks/useEventEmitter";

import { FilterContextProvider } from "./FilterContext";
import FilterList from "./components/FilterList";
import { getFilterDefault } from "./filters";
import { useFilterList } from "./hooks/useFilterList";
import { useFilterOptions } from "./hooks/useFilterOptions";
import { FilterData, FilterFieldKey, FilterSourceData } from "./typing";

interface FilterBarProps {
  dataSourceList: FilterSourceData;
  onChange: (result: FilterSourceData, filter?: FilterData) => void;
  initValues?: Partial<FilterData>;
  category?: ApiCreditCardsResponse["categories"];
  banks?: ApiCreditCardsResponse["banks"];
}

const FilterBar = (props: FilterBarProps) => {
  const { dataSourceList, onChange, category, banks } = props;
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const [open, setOpen] = useState(false);
  const [fieldId, setFieldId] = useState<FilterFieldKey>("all");

  const defaultFilterData = useRef<FilterData>(getFilterDefault());
  const [filterData, setFilterData] = useState<FilterData>(() => {
    return {
      ...defaultFilterData.current,
      ...props.initValues,
      name: "",
    };
  });

  const filterOptions = useFilterOptions({ category, banks });
  const data = useFilterList(dataSourceList, filterData);

  useEffect(() => {
    onChange && onChange(data);
  }, [data, onChange]);

  useListenEvent(CREDIT_CARD_PAGE_CHANGE_KEYWORDS, (v: string) => {
    setFilterData({
      ...filterData,
      name: v,
    });
  });

  const resetFilterField = useEvent((key: keyof FilterData) => {
    setFilterData({
      ...filterData,
      [key]: defaultFilterData.current[key],
    });
  });

  const resetFilterData = useEvent(() => {
    setFilterData({
      ...defaultFilterData.current,
    });
  });

  const handleChangeFilter = useCallback(
    (value: Partial<FilterData>) => {
      setFilterData({
        ...filterData,
        ...value,
      });
    },
    [filterData]
  );

  return (
    <FilterContextProvider
      value={{
        filterOptions,
        filterData,
        defaultFilterData: defaultFilterData.current,
        setFilterData: handleChangeFilter,
        anchorEl,
        setAnchorEl,
        open,
        setOpen,
        fieldId,
        setFieldId,

        resetFilterField,
        resetFilterData,
      }}
    >
      <FilterList />
    </FilterContextProvider>
  );
};

export default React.memo(FilterBar);
